// src/admin/components/apartments/ApartmentDetailView.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  RiPencilLine,
  RiDeleteBin6Line,
  RiArrowLeftLine,
  RiCheckLine,
  RiCloseCircleLine,
  RiAlertLine,
  RiRefreshLine,
  RiRulerLine,
  RiBuilding3Line,
  RiLayoutMasonryLine,
  RiBox3Line,
  RiLightbulbLine,
  RiUser3Line,
  RiCalendarLine,
  RiFileListLine,
  RiInformationLine,
  RiMoneyDollarCircleLine,
  RiTimeLine,
  RiCalendarCheckLine,
  RiArchiveDrawerLine,
} from 'react-icons/ri';
import {
  calculateLeaseSummary,
  getRentStatusColor as getTenantRentStatusColor,
  formatCurrency as formatCurrencyTenant,
  isLeaseExpired,
  getTenantLeaseStatus
} from '../../../models/tenantModel';
import {
  getApartment,
  deleteApartment,
  updateApartmentStatus,
  getApartmentStatusColor,
  APARTMENT_STATUS_OPTIONS
} from '../../../models/apartmentModel';
import { useTheme } from '../../../contexts/ThemeContext';
import { getCurrentUser } from '../../../models/authModel';
import { bookingsAPI, tenantsAPI } from '../../../services/api';

const ApartmentDetailView = () => {
  const { t, i18n } = useTranslation();
  const { isDarkMode } = useTheme();
  const { id } = useParams();
  const navigate = useNavigate();
  const isRTL = i18n.language === 'ar' || i18n.language === 'ku';

  const [apartment, setApartment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ isOpen: false, apartmentId: null, apartmentNumber: '' });
  const [deleting, setDeleting] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [loadingBookings, setLoadingBookings] = useState(false);
  const [pastTenants, setPastTenants] = useState([]);
  const [loadingPastTenants, setLoadingPastTenants] = useState(false);

  // Fetch showroom data
  const fetchApartmentData = async () => {
    try {
      setLoading(true); setError(null);
      const response = await getApartment(id);
      if (response?.apartment) {
        setApartment(response.apartment);
      } else {
        setError(t('showroom_unit_not_found', 'Showroom unit not found'));
      }
    } catch (err) {
      console.error('Error fetching showroom details:', err);
      const errorMessages = {
        401: t('authentication_failed', 'Authentication failed. Please log in again.'),
        403: t('access_denied', 'Access denied. Admin role required.'),
        404: t('showroom_unit_not_found', 'Showroom unit not found.'),
        500: t('server_error', 'Server error. Please check if the backend is running.'),
      };
      setError(errorMessages[err.response?.status] ||
        (err.message?.includes('Network Error') || err.code === 'ERR_NETWORK'
          ? t('cannot_connect_to_server', 'Cannot connect to server. Please check your network connection.')
          : t('failed_load_showroom', 'Failed to load showroom details')));
    } finally { setLoading(false); }
  };

  useEffect(() => {
    fetchApartmentData();
    fetchBookingsData();
    fetchPastTenantsData();
  }, [id]);

  const fetchPastTenantsData = async () => {
    try {
      setLoadingPastTenants(true);
      const response = await tenantsAPI.getAll({ apartmentId: id, status: 'expired' });
      if (response?.tenants) setPastTenants(response.tenants);
    } catch (err) {
      console.error('Error fetching past clients:', err);
    } finally { setLoadingPastTenants(false); }
  };

  const fetchBookingsData = async () => {
    try {
      setLoadingBookings(true);
      const response = await bookingsAPI.getAll({ apartmentId: id });
      if (response?.bookings) setBookings(response.bookings);
    } catch (err) {
      console.error('Error fetching holds/bookings:', err);
    } finally { setLoadingBookings(false); }
  };

  const openDeleteDialog = () => {
    if (!apartment) return;
    setDeleteDialog({ isOpen: true, apartmentId: apartment.id, apartmentNumber: `${apartment.buildingName} - ${apartment.apartmentNumber}` });
  };
  const closeDeleteDialog = () => setDeleteDialog({ isOpen: false, apartmentId: null, apartmentNumber: '' });

  const handleDeleteApartment = async () => {
    if (!deleteDialog.apartmentId) return;
    try {
      setDeleting(true);
      await deleteApartment(deleteDialog.apartmentId);
      navigate('/admin/apartments');
    } catch (err) {
      console.error('Error deleting showroom:', err);
      setError(err.message || t('failed_delete_showroom', 'Failed to delete showroom'));
      closeDeleteDialog();
    } finally { setDeleting(false); }
  };

  const toggleApartmentStatus = async (newStatus) => {
    if (!apartment || statusUpdating) return;
    try {
      setStatusUpdating(true);
      await updateApartmentStatus(apartment.id, newStatus);
      setApartment({ ...apartment, status: newStatus });
    } catch (err) {
      console.error('Error updating showroom status:', err);
      setError(err.message || t('failed_update_status', 'Failed to update showroom status'));
    } finally { setStatusUpdating(false); }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Intl.DateTimeFormat('en-US', { year: 'numeric', month: 'short', day: 'numeric' }).format(new Date(dateString));
  };

  const getNextStatusOptions = () => {
    if (!apartment) return [];
    switch (apartment.status) {
      case 'vacant':      return [{ value: 'occupied', label: t('rented_sold', 'Rented / Sold') }, { value: 'maintenance', label: t('under_maintenance', 'Under Maintenance') }];
      case 'occupied':    return [{ value: 'vacant', label: t('vacant_unsold', 'Vacant / Unsold') }, { value: 'maintenance', label: t('under_maintenance', 'Under Maintenance') }];
      case 'maintenance': return [{ value: 'vacant', label: t('vacant_unsold', 'Vacant / Unsold') }, { value: 'occupied', label: t('rented_sold', 'Rented / Sold') }];
      default:            return APARTMENT_STATUS_OPTIONS.filter(o => o.value !== apartment.status);
    }
  };

  const getStatusBgColor = (status) => {
    switch (status) {
      case 'vacant':      return isDarkMode ? 'bg-blue-900/10 border-blue-900/50'    : 'bg-blue-50 border-blue-100';
      case 'occupied':    return isDarkMode ? 'bg-emerald-900/10 border-emerald-900/50' : 'bg-emerald-50 border-emerald-100';
      case 'maintenance': return isDarkMode ? 'bg-amber-900/10 border-amber-900/50'  : 'bg-amber-50 border-amber-100';
      default:            return isDarkMode ? 'bg-zinc-900 border-zinc-800'          : 'bg-zinc-50 border-zinc-200';
    }
  };

  if (loading) return (
    <div dir={isRTL?'rtl':'ltr'} className={`rounded-xl border shadow-lg p-16 text-center ${isDarkMode?'bg-zinc-900 border-zinc-800':'bg-white border-zinc-200'}`}>
      <div className="flex flex-col items-center justify-center space-y-4">
        <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-red-500 border-t-transparent"/>
        <p className={`text-lg font-medium ${isDarkMode?'text-zinc-300':'text-zinc-600'}`}>{t('loading_showroom_data', 'Loading showroom data…')}</p>
      </div>
    </div>
  );

  if (error) return (
    <div dir={isRTL?'rtl':'ltr'} className={`rounded-xl border shadow-lg p-8 ${isDarkMode?'bg-zinc-900 border-zinc-800':'bg-white border-zinc-200'}`}>
      <div className={`p-6 rounded-lg flex flex-col items-center text-center ${isDarkMode?'bg-red-900/20 text-red-400 border border-red-800':'bg-red-50 text-red-700 border border-red-200'}`}>
        <RiAlertLine className="text-4xl mb-4"/>
        <h3 className="text-lg font-bold mb-2">{t('notice', 'Notice')}</h3>
        <p className="mb-6 font-medium">{error}</p>
        <div className="flex gap-4">
          <button onClick={fetchApartmentData} className={`px-5 py-2.5 rounded-lg font-semibold flex items-center ${isDarkMode?'bg-red-700 text-white hover:bg-red-600':'bg-red-600 text-white hover:bg-red-700'}`}>
            <RiRefreshLine className="me-2"/> {t('try_again', 'Try Again')}
          </button>
          <button onClick={()=>navigate('/admin/apartments')} className={`px-5 py-2.5 rounded-lg font-semibold flex items-center ${isDarkMode?'bg-zinc-800 text-white hover:bg-zinc-700':'bg-zinc-200 text-zinc-800 hover:bg-zinc-300'}`}>
            <RiArrowLeftLine className="me-2"/> {t('back_to_showrooms', 'Back to Showrooms')}
          </button>
        </div>
      </div>
    </div>
  );

  if (!apartment) return (
    <div dir={isRTL?'rtl':'ltr'} className={`rounded-xl border shadow-lg p-16 text-center ${isDarkMode?'bg-zinc-900 border-zinc-800':'bg-white border-zinc-200'}`}>
      <div className="flex flex-col items-center justify-center space-y-4">
        <RiInformationLine className={`text-5xl ${isDarkMode?'text-zinc-600':'text-zinc-400'}`}/>
        <h3 className={`text-xl font-bold ${isDarkMode?'text-zinc-300':'text-zinc-700'}`}>{t('showroom_not_found_title', 'Showroom Not Found')}</h3>
        <p className={`font-medium ${isDarkMode?'text-zinc-500':'text-zinc-500'}`}>{t('showroom_not_found_desc', 'The unit you are looking for does not exist or has been removed.')}</p>
        <button onClick={()=>navigate('/admin/apartments')} className={`px-5 py-2.5 mt-4 rounded-lg font-semibold flex items-center ${isDarkMode?'bg-red-600 text-white hover:bg-red-700':'bg-zinc-500 text-white hover:bg-red-600'}`}>
          <RiArrowLeftLine className="me-2"/> {t('back_to_showrooms', 'Back to Showrooms')}
        </button>
      </div>
    </div>
  );

  return (
    <div dir={isRTL?'rtl':'ltr'} className="space-y-6">

      {/* Delete Dialog */}
      {deleteDialog.isOpen && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
          <div className={`rounded-xl border shadow-2xl max-w-md w-full p-6 ${isDarkMode?'bg-zinc-900 border-zinc-700':'bg-white border-zinc-200'}`}>
            <div className="flex items-center mb-4 text-red-500">
              <RiAlertLine size={24} className="me-2"/>
              <h3 className="text-lg font-bold">{t('confirm_deletion', 'Confirm Deletion')}</h3>
            </div>
            <p className={`mb-6 font-medium ${isDarkMode?'text-zinc-300':'text-zinc-700'}`}>
              {t('confirm_delete_showroom_p1', 'Are you sure you want to permanently delete showroom ')} <span className="font-bold">"{deleteDialog.apartmentNumber}"</span>{t('confirm_delete_showroom_p2', '? This action cannot be undone.')}
            </p>
            <div className="flex justify-end gap-3">
              <button onClick={closeDeleteDialog} disabled={deleting} className={`px-5 py-2.5 rounded-lg font-semibold transition-colors ${isDarkMode?'bg-zinc-800 text-zinc-300 hover:bg-zinc-700':'bg-zinc-200 text-zinc-700 hover:bg-zinc-300'}`}>
                {t('cancel', 'Cancel')}
              </button>
              <button onClick={handleDeleteApartment} disabled={deleting} className={`px-5 py-2.5 rounded-lg font-semibold bg-red-600 text-white hover:bg-red-700 flex items-center shadow-md shadow-red-500/20 transition-all ${deleting?'opacity-70 cursor-not-allowed':''}`}>
                {deleting ? (<><div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin me-2"/> {t('deleting', 'Deleting…')}</>) : (<><RiDeleteBin6Line className="me-2"/> {t('delete_unit', 'Delete Unit')}</>)}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main card */}
      <div className={`rounded-xl border shadow-lg overflow-hidden ${isDarkMode?'bg-zinc-900 border-zinc-800':'bg-white border-zinc-200'}`}>
        <div className="h-1.5 w-full bg-gradient-to-r from-red-600 via-orange-500 to-red-600"/>

        {/* Header */}
        <div className={`p-5 md:p-6 border-b ${isDarkMode?'bg-zinc-900/50 border-zinc-800':'bg-zinc-50 border-zinc-100'}`}>
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-5">
            <div className="flex items-center gap-4">
              <button onClick={()=>navigate('/admin/apartments')} className={`p-2.5 rounded-full shrink-0 transition-colors ${isDarkMode?'bg-zinc-800 text-zinc-400 hover:text-white hover:bg-zinc-700':'bg-zinc-100 text-zinc-600 hover:text-zinc-900 hover:bg-zinc-200'}`} title={t('back', 'Back')}>
                <RiArrowLeftLine size={22} className="rtl:rotate-180"/>
              </button>
              <div>
                <h2 className={`text-2xl font-black tracking-tight font-mono uppercase flex items-center ${isDarkMode?'text-white':'text-zinc-900'}`}>
                  <RiBox3Line className={`me-3 text-3xl ${isDarkMode?'text-red-400':'text-red-600'}`}/>
                  {apartment.apartmentNumber}
                </h2>
                <div className="flex items-center mt-1.5 font-medium">
                  <RiBuilding3Line className={`me-1.5 ${isDarkMode?'text-zinc-500':'text-zinc-400'}`} size={16}/>
                  <span className={`${isDarkMode?'text-zinc-400':'text-zinc-600'}`}>{apartment.buildingName||t('unknown_complex', 'Unknown Complex')}</span>
                </div>
              </div>
              <div className="hidden md:flex items-center gap-2 ms-4">
                <span className={`px-3 py-1 text-[10px] font-bold uppercase tracking-widest rounded-md border ${(apartment.tenants?.[0]?.name||apartment.tenantName)
                  ? getTenantRentStatusColor(apartment.tenants?.[0]?.rentStatus||apartment.rentStatus||'pending',isDarkMode)
                  : getApartmentStatusColor(apartment.status,isDarkMode)}`}>
                  {(apartment.tenants?.[0]?.name||apartment.tenantName) ? t('rented_sold', 'Rented / Sold') : t(`status_${apartment.status}`)||apartment.status}
                </span>
                {(apartment.tenants?.[0]?.name||apartment.tenantName) && isLeaseExpired(apartment) && (
                  <span className={`px-3 py-1 text-[10px] font-bold uppercase tracking-widest rounded-md border ${isDarkMode?'bg-red-900/30 text-red-400 border-red-800/50':'bg-red-50 text-red-700 border-red-200'}`}>
                    {t('contract_expired', 'Contract Expired')}
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Link to={`/admin/apartments/${apartment.id}/edit`} className={`px-4 py-2.5 text-sm font-semibold rounded-lg border flex items-center transition-colors ${isDarkMode?'bg-zinc-800 border-zinc-700 text-zinc-300 hover:bg-zinc-700':'bg-white border-zinc-200 text-zinc-700 hover:bg-zinc-50'}`}>
                <RiPencilLine className="me-2"/> {t('configure', 'Configure')}
              </Link>
              {getCurrentUser()?.role==='admin' && (
                <button onClick={openDeleteDialog} className={`px-4 py-2.5 text-sm font-semibold rounded-lg flex items-center transition-colors ${isDarkMode?'bg-red-900/20 text-red-400 hover:bg-red-900/40 border border-red-900/50':'bg-red-50 text-red-600 hover:bg-red-100 border border-red-200'}`}>
                  <RiDeleteBin6Line className="me-2"/> {t('delete', 'Delete')}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-5 md:p-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

            {/* Left column */}
            <div className="lg:col-span-2 space-y-6">

              {/* Complex & Zoning */}
              <div className={`p-6 rounded-xl border ${isDarkMode?'bg-zinc-800/50 border-zinc-700/50':'bg-zinc-50 border-zinc-200/60'}`}>
                <h3 className={`text-xs font-black uppercase tracking-widest mb-5 pb-2 border-b flex items-center justify-start ${isDarkMode?'text-zinc-400 border-zinc-700':'text-zinc-500 border-zinc-200'}`}>
                  <RiBuilding3Line className="me-2 text-lg"/> <span>{t('complex_zoning', 'Complex & Zoning')}</span>
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="text-start">
                    <h4 className={`text-xs font-bold uppercase tracking-wider mb-1 ${isDarkMode?'text-zinc-500/80':'text-zinc-500'}`}>{t('complex_park', 'Complex / Park')}</h4>
                    <p className={`text-lg font-bold ${isDarkMode?'text-white':'text-zinc-900'}`}>{apartment.buildingName||'N/A'}</p>
                  </div>
                  <div className="text-start">
                    <h4 className={`text-xs font-bold uppercase tracking-wider mb-1 ${isDarkMode?'text-zinc-500/80':'text-zinc-500'}`}>{t('block_zone', 'Block / Zone')}</h4>
                    <p className={`text-lg font-bold font-mono ${isDarkMode?'text-white':'text-zinc-900'}`}>{apartment.floor||'N/A'}</p>
                  </div>
                  {apartment.buildingAddress && (
                    <div className="md:col-span-2 text-start">
                      <h4 className={`text-xs font-bold uppercase tracking-wider mb-1 ${isDarkMode?'text-zinc-500/80':'text-zinc-500'}`}>{t('physical_address', 'Physical Address')}</h4>
                      <p className={`text-md font-medium ${isDarkMode?'text-zinc-300':'text-zinc-700'}`}>{apartment.buildingAddress}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Showroom Specs */}
              <div className={`p-6 rounded-xl border ${isDarkMode?'bg-zinc-800/50 border-zinc-700/50':'bg-zinc-50 border-zinc-200/60'}`}>
                <h3 className={`text-xs font-black uppercase tracking-widest mb-5 pb-2 border-b flex items-center justify-start ${isDarkMode?'text-zinc-400 border-zinc-700':'text-zinc-500 border-zinc-200'}`}>
                  <RiBox3Line className="me-2 text-lg"/> <span>{t('showroom_specifications', 'Showroom Specifications')}</span>
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="text-start">
                    <h4 className={`text-xs font-bold uppercase tracking-wider mb-1 ${isDarkMode?'text-zinc-500/80':'text-zinc-500'}`}>{t('class_type', 'Class Type')}</h4>
                    <span className={`inline-flex px-2.5 py-1 text-xs font-bold uppercase tracking-wider rounded-md border ${isDarkMode?'bg-zinc-900 text-zinc-300 border-zinc-700':'bg-white text-zinc-700 border-zinc-300'}`}>
                      {apartment.type||'N/A'}
                    </span>
                  </div>
                  <div className="text-start">
                    <h4 className={`text-xs font-bold uppercase tracking-wider mb-1 ${isDarkMode?'text-zinc-500/80':'text-zinc-500'}`}>{t('internal_size', 'Internal Size')}</h4>
                    <p className={`text-lg font-bold font-mono ${isDarkMode?'text-white':'text-zinc-900'}`}>{apartment.size?`${apartment.size} m²`:'N/A'}</p>
                  </div>
                  <div className="text-start">
                    <h4 className={`text-xs font-bold uppercase tracking-wider mb-1 ${isDarkMode?'text-zinc-500/80':'text-zinc-500'}`}>{t('utilities_active', 'Utilities Active')}</h4>
                    <div className="flex items-center gap-2 mt-1 justify-start">
                      {apartment.isFurnished
                        ? <><RiLightbulbLine className="text-yellow-500 text-xl"/><span className={`font-bold ${isDarkMode?'text-zinc-300':'text-zinc-700'}`}>{t('yes_pwr_clim', 'Yes (Pwr/Clim)')}</span></>
                        : <><RiLightbulbLine className={`text-xl ${isDarkMode?'text-zinc-600':'text-zinc-400'}`}/><span className={`font-bold ${isDarkMode?'text-zinc-500':'text-zinc-500'}`}>{t('none', 'None')}</span></>
                      }
                    </div>
                  </div>
                </div>
              </div>

              {/* Owner */}
              {apartment.ownerName && (
                <div className={`p-6 rounded-xl border ${isDarkMode?'bg-zinc-800/50 border-zinc-700/50':'bg-zinc-50 border-zinc-200/60'}`}>
                  <h3 className={`text-xs font-black uppercase tracking-widest mb-5 pb-2 border-b flex items-center justify-start ${isDarkMode?'text-zinc-400 border-zinc-700':'text-zinc-500 border-zinc-200'}`}>
                    <RiUser3Line className="me-2 text-lg"/> <span>{t('owner_investor_details', 'Owner / Investor Details')}</span>
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="text-start">
                      <h4 className={`text-xs font-bold uppercase tracking-wider mb-1 ${isDarkMode?'text-zinc-500/80':'text-zinc-500'}`}>{t('entity_name', 'Entity Name')}</h4>
                      <p className={`text-lg font-bold ${isDarkMode?'text-white':'text-zinc-900'}`}>{apartment.ownerName}</p>
                    </div>
                    <div className="text-start">
                      <h4 className={`text-xs font-bold uppercase tracking-wider mb-1 ${isDarkMode?'text-zinc-500/80':'text-zinc-500'}`}>{t('contact_number', 'Contact Number')}</h4>
                      <p className={`text-lg font-bold font-mono ${isDarkMode?'text-white':'text-zinc-900'}`}>{apartment.ownerPhone||t('no_phone', 'No Phone')}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Active client leases */}
              {apartment.tenants?.length>0 && apartment.tenants
                .filter(ten => { const s=getTenantLeaseStatus(ten); return s==='current'||s==='upcoming'; })
                .map((tenant, index) => {
                  const leaseStatus = getTenantLeaseStatus(tenant);
                  const isCurrent   = leaseStatus === 'current';
                  return (
                    <div key={tenant.id} className={`p-6 rounded-xl border ${isCurrent?(isDarkMode?'bg-zinc-800 border-zinc-700':'bg-white border-zinc-300 shadow-md'):(isDarkMode?'bg-blue-900/10 border-blue-800/50':'bg-blue-50/50 border-blue-200')} ${index>0?'mt-6':''}`}>
                      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6 pb-4 border-b border-zinc-500/20">
                        <h3 className={`text-lg font-bold uppercase tracking-widest flex items-center ${isCurrent?(isDarkMode?'text-zinc-100':'text-zinc-800'):(isDarkMode?'text-blue-400':'text-blue-700')}`}>
                          <RiUser3Line className={`me-2 text-2xl ${isCurrent?'text-red-500':'text-blue-500'}`}/>
                          {leaseStatus==='upcoming'?t('future_client_upcoming', 'Future Client (Upcoming)'):t('active_client', 'Active Client')}
                        </h3>
                        <Link to={`/admin/tenants/${tenant.id}`} className={`px-4 py-2 rounded-lg text-sm font-bold flex items-center transition-all ${isDarkMode?'bg-zinc-900 border border-zinc-700 text-zinc-300 hover:text-white hover:border-zinc-500':'bg-zinc-100 border border-zinc-200 text-zinc-700 hover:bg-zinc-200'}`}>
                          <RiFileListLine className="me-2"/> {t('view_client_profile', 'View Client Profile')}
                        </Link>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                        <div><h4 className={`text-xs font-bold uppercase tracking-wider mb-1 ${isDarkMode?'text-zinc-500':'text-zinc-500'}`}>{t('entity_name', 'Entity Name')}</h4><p className={`text-lg font-black ${isDarkMode?'text-white':'text-zinc-900'}`}>{tenant.name}</p></div>
                        <div><h4 className={`text-xs font-bold uppercase tracking-wider mb-1 ${isDarkMode?'text-zinc-500':'text-zinc-500'}`}>{t('phone', 'Phone')}</h4><p className={`text-md font-mono font-medium ${isDarkMode?'text-zinc-300':'text-zinc-700'}`}>{tenant.phone||'N/A'}</p></div>
                        <div><h4 className={`text-xs font-bold uppercase tracking-wider mb-1 ${isDarkMode?'text-zinc-500':'text-zinc-500'}`}>{t('email', 'Email')}</h4><p className={`text-md font-medium truncate ${isDarkMode?'text-zinc-300':'text-zinc-700'}`}>{tenant.email||'N/A'}</p></div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        {/* Contract Specs */}
                        <div className="space-y-4">
                          <h4 className={`text-sm font-bold uppercase tracking-widest flex items-center ${isDarkMode?'text-zinc-400':'text-zinc-600'}`}>
                            <RiCalendarCheckLine className={`me-2 text-lg ${index===0?'text-red-500':'text-blue-500'}`}/> {t('contract_specs', 'Contract Specs')}
                          </h4>
                          <div className="grid grid-cols-2 gap-3">
                            {[
                              {label:t('start_date', 'Start Date'),    val:formatDate(tenant.contractStartDate)},
                              {label:t('end_date', 'End Date'),      val:formatDate(tenant.contractEndDate)},
                              {label:t('billing_cycle', 'Billing Cycle'), val:`${t('day', 'Day')} ${tenant.paymentDate}`},
                              {label:t('term_length', 'Term Length'),   val:tenant.contractStartDate&&tenant.contractEndDate?`${calculateLeaseSummary({contractStartDate:tenant.contractStartDate,contractEndDate:tenant.contractEndDate}).durationMonths} ${t('months', 'Months')}`:'N/A'},
                            ].map(item=>(
                              <div key={item.label} className={`p-3 rounded-lg border ${isDarkMode?'bg-zinc-900/50 border-zinc-700':'bg-zinc-50 border-zinc-200'}`}>
                                <p className={`text-[10px] uppercase font-black tracking-widest mb-1 ${isDarkMode?'text-zinc-500':'text-zinc-500'}`}>{item.label}</p>
                                <p className={`text-sm font-bold font-mono ${isDarkMode?'text-zinc-200':'text-zinc-800'}`}>{item.val}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                        {/* Financial Ledger */}
                        <div className="space-y-4">
                          <h4 className={`text-sm font-bold uppercase tracking-widest flex items-center ${isDarkMode?'text-zinc-400':'text-zinc-600'}`}>
                            <RiMoneyDollarCircleLine className="me-2 text-lg text-emerald-500"/> {t('financial_ledger', 'Financial Ledger')}
                          </h4>
                          <div className={`p-4 rounded-lg border space-y-3 ${isDarkMode?'bg-zinc-900/50 border-zinc-700':'bg-white border-zinc-200'}`}>
                            <div className="flex justify-between items-center text-sm">
                              <span className={`font-semibold ${isDarkMode?'text-zinc-400':'text-zinc-500'}`}>{t('base_rate', 'Base Rate')}</span>
                              <span className={`font-mono font-bold ${isDarkMode?'text-zinc-200':'text-zinc-800'}`}>{formatCurrencyTenant(tenant.rent)||'N/A'}</span>
                            </div>
                            <div className="flex justify-between items-center text-sm pt-3 border-t border-zinc-500/20">
                              <span className={`font-bold uppercase tracking-wider ${isDarkMode?'text-zinc-300':'text-zinc-700'}`}>{t('net_effective', 'Net Effective')}</span>
                              <span className={`font-mono font-black text-lg ${isDarkMode?'text-white':'text-zinc-900'}`}>{formatCurrencyTenant(tenant.rentAfterDiscount)||'N/A'}</span>
                            </div>
                            <div className={`mt-3 p-2.5 rounded-md flex justify-between items-center ${isDarkMode?'bg-emerald-900/20 border border-emerald-900/50':'bg-emerald-50 border border-emerald-100'}`}>
                              <span className={`text-[10px] font-black uppercase tracking-widest ${isDarkMode?'text-emerald-400':'text-emerald-700'}`}>{t('clear_until', 'Clear Until')}</span>
                              <span className={`text-sm font-bold font-mono ${isDarkMode?'text-emerald-300':'text-emerald-800'}`}>{formatDate(tenant.nextBillingDate)}</span>
                            </div>
                            <div className="flex justify-between items-center pt-1">
                              <span className={`text-xs font-bold uppercase tracking-wider ${isDarkMode?'text-zinc-500':'text-zinc-500'}`}>{t('acct_status', 'Acct Status')}</span>
                              <span className={`px-2 py-1 rounded text-[10px] font-black uppercase tracking-widest border ${getTenantRentStatusColor(tenant.rentStatus,isDarkMode)}`}>
                                {tenant.rentStatus||'Unknown'}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}

              {/* System Timestamps */}
              <div className={`p-6 rounded-xl border ${isDarkMode?'bg-zinc-800/50 border-zinc-700/50':'bg-zinc-50 border-zinc-200/60'}`}>
                <h3 className={`text-xs font-black uppercase tracking-widest mb-5 pb-2 border-b flex items-center ${isDarkMode?'text-zinc-400 border-zinc-700':'text-zinc-500 border-zinc-200'}`}>
                  <RiInformationLine className="me-2 text-lg"/> {t('system_timestamps', 'System Timestamps')}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div><h4 className={`text-xs font-bold uppercase tracking-wider mb-1 ${isDarkMode?'text-zinc-500':'text-zinc-500'}`}>{t('logged_on', 'Logged On')}</h4><p className={`font-mono text-sm ${isDarkMode?'text-zinc-300':'text-zinc-700'}`}>{apartment.createdAt?formatDate(apartment.createdAt):'N/A'}</p></div>
                  <div><h4 className={`text-xs font-bold uppercase tracking-wider mb-1 ${isDarkMode?'text-zinc-500':'text-zinc-500'}`}>{t('last_modification', 'Last Modification')}</h4><p className={`font-mono text-sm ${isDarkMode?'text-zinc-300':'text-zinc-700'}`}>{apartment.updatedAt?formatDate(apartment.updatedAt):'N/A'}</p></div>
                </div>
              </div>

              {/* Queue & Holds */}
              <div className={`p-6 rounded-xl border ${isDarkMode?'bg-zinc-800/50 border-zinc-700/50':'bg-zinc-50 border-zinc-200/60'}`}>
                <div className="flex justify-between items-center mb-5 pb-2 border-b border-zinc-500/20">
                  <h3 className={`text-xs font-black uppercase tracking-widest flex items-center ${isDarkMode?'text-zinc-400':'text-zinc-500'}`}>
                    <RiCalendarLine className="me-2 text-lg"/> {t('queue_holds', 'Queue & Holds')}
                  </h3>
                  <Link to={`/admin/bookings/add?apartmentId=${apartment.id}`} className={`text-xs font-bold uppercase tracking-wider transition-colors ${isDarkMode?'text-red-400 hover:text-red-300':'text-red-600 hover:text-red-700'}`}>
                    + {t('log_new_hold', 'Log New Hold')}
                  </Link>
                </div>
                {loadingBookings ? (
                  <div className="flex justify-center p-4"><div className="animate-spin rounded-full h-6 w-6 border-2 border-red-500 border-t-transparent"/></div>
                ) : bookings.length===0 ? (
                  <p className={`text-sm font-medium italic ${isDarkMode?'text-zinc-500':'text-zinc-500'}`}>{t('no_active_queue', 'No active queue or holds for this unit.')}</p>
                ) : (
                  <div className="space-y-3">
                    {bookings.slice(0,5).map(booking=>(
                      <div key={booking.id} className={`p-3.5 rounded-lg border flex justify-between items-center ${isDarkMode?'bg-zinc-900 border-zinc-700':'bg-white border-zinc-200'}`}>
                        <div>
                          <p className={`font-bold text-sm ${isDarkMode?'text-zinc-200':'text-zinc-900'}`}>{booking.prospectName}</p>
                          <p className={`text-xs font-mono mt-0.5 ${isDarkMode?'text-zinc-500':'text-zinc-500'}`}>
                            {new Date(booking.moveInDate).toLocaleDateString()} - {booking.moveOutDate?new Date(booking.moveOutDate).toLocaleDateString():t('indefinite', 'Indefinite')}
                          </p>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className={`inline-flex items-center px-2.5 py-1 rounded text-[10px] font-black uppercase tracking-widest ${
                            booking.status==='confirmed'?'bg-blue-500/10 text-blue-500 border border-blue-500/20':
                            booking.status==='pending'?'bg-yellow-500/10 text-yellow-600 border border-yellow-500/20':
                            booking.status==='converted'?'bg-emerald-500/10 text-emerald-500 border border-emerald-500/20':
                            'bg-zinc-500/10 text-zinc-500 border border-zinc-500/20'
                          }`}>{t(`status_${booking.status}`)||booking.status}</span>
                          <Link to={`/admin/bookings/${booking.id}`} className={`text-xs font-bold uppercase tracking-wider ${isDarkMode?'text-red-400 hover:text-red-300':'text-red-600 hover:text-red-700'}`}>{t('view', 'View')}</Link>
                        </div>
                      </div>
                    ))}
                    {bookings.length>5&&(
                      <div className="text-center pt-3">
                        <Link to={`/admin/bookings?apartmentId=${apartment.id}`} className={`text-xs font-bold uppercase tracking-widest transition-colors ${isDarkMode?'text-red-400 hover:text-white':'text-red-600 hover:text-zinc-900'}`}>
                          {t('view_full_queue', 'View Full Queue')} ({bookings.length})
                        </Link>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Right column */}
            <div className="space-y-6">
              {/* Status Override */}
              <div className={`p-6 rounded-xl border ${getStatusBgColor(apartment.status)}`}>
                <h3 className={`text-xs font-black uppercase tracking-widest mb-5 flex items-center ${isDarkMode?'text-zinc-300':'text-zinc-700'}`}>
                  <RiCheckLine className="me-2 text-lg"/> {t('status_override', 'Status Override')}
                </h3>
                <div className="space-y-5">
                  <div className={`p-4 rounded-xl border ${isDarkMode?'bg-zinc-900/80 border-zinc-800':'bg-white border-zinc-200/80 shadow-sm'}`}>
                    <div className="flex items-start gap-3">
                      <div className={`p-2.5 rounded-lg shrink-0 ${
                        apartment.status==='vacant'?(isDarkMode?'bg-emerald-900/30 text-emerald-400':'bg-emerald-100 text-emerald-600'):
                        (apartment.status==='occupied'||apartment.tenantName)?(isDarkMode?'bg-red-900/30 text-red-400':'bg-red-50 text-red-600'):
                        (isDarkMode?'bg-amber-900/30 text-amber-400':'bg-amber-100 text-amber-600')
                      }`}>
                        {apartment.status==='vacant' && <RiCheckLine size={24}/>}
                        {(apartment.status==='occupied'||apartment.tenantName) && <RiBox3Line size={24}/>}
                        {apartment.status==='maintenance' && <RiCloseCircleLine size={24}/>}
                      </div>
                      <div>
                        <p className={`font-black uppercase tracking-wider ${
                          (apartment.status==='occupied'||apartment.tenantName)?(isDarkMode?'text-red-400':'text-red-700'):
                          apartment.status==='vacant'?(isDarkMode?'text-emerald-400':'text-emerald-700'):
                          (isDarkMode?'text-amber-400':'text-amber-700')
                        }`}>{apartment.tenantName?t('rented_sold', 'Rented / Sold'):t(`status_${apartment.status}`)||apartment.status}</p>
                        <p className={`text-xs mt-1 font-medium leading-relaxed ${
                          (apartment.status==='occupied'||apartment.tenantName)?(isDarkMode?'text-red-300/70':'text-red-600/80'):
                          apartment.status==='vacant'?(isDarkMode?'text-emerald-300/70':'text-emerald-600/80'):
                          (isDarkMode?'text-amber-300/70':'text-amber-600/80')
                        }`}>
                          {apartment.tenantName&&isLeaseExpired(apartment)?t('contract_exceeded_desc', 'Contract has exceeded end date.'):
                           (apartment.tenantName||apartment.status==='occupied')?t('secured_by_client', 'Currently secured by a client.'):
                           apartment.status==='vacant'?t('available_for_logging', 'Free and available for logging.'):
                           t('locked_for_maintenance', 'Slot locked for maintenance.')}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div>
                    <h4 className={`text-[10px] font-black uppercase tracking-widest mb-3 ${isDarkMode?'text-zinc-500':'text-zinc-500'}`}>{t('force_status_change', 'Force Status Change')}</h4>
                    <div className="grid grid-cols-1 gap-2.5">
                      {getNextStatusOptions().map(option=>(
                        <button key={option.value} onClick={()=>toggleApartmentStatus(option.value)} disabled={statusUpdating}
                          className={`px-4 py-3 text-xs font-bold uppercase tracking-widest rounded-lg flex items-center justify-center transition-all ${
                            option.value==='vacant'?(isDarkMode?'bg-emerald-900/20 text-emerald-400 border border-emerald-900/50 hover:bg-emerald-900/40':'bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100'):
                            option.value==='occupied'?(isDarkMode?'bg-red-900/20 text-red-400 border border-red-900/50 hover:bg-red-900/40':'bg-red-50 text-red-700 border border-red-200 hover:bg-red-100'):
                            (isDarkMode?'bg-amber-900/20 text-amber-400 border border-amber-900/50 hover:bg-amber-900/40':'bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100')
                          } ${statusUpdating?'opacity-50 cursor-not-allowed':''}`}>
                          {statusUpdating
                            ? <div className="h-4 w-4 border-2 border-current border-t-transparent rounded-full animate-spin me-2"/>
                            : <>
                                {option.value==='vacant'&&<RiCheckLine className="me-2 text-lg"/>}
                                {option.value==='occupied'&&<RiBox3Line className="me-2 text-lg"/>}
                                {option.value==='maintenance'&&<RiCloseCircleLine className="me-2 text-lg"/>}
                              </>
                          }
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Command Center */}
              <div className={`p-6 rounded-xl border ${isDarkMode?'bg-zinc-800/50 border-zinc-700/50':'bg-zinc-50 border-zinc-200/60'}`}>
                <h3 className={`text-xs font-black uppercase tracking-widest mb-5 ${isDarkMode?'text-zinc-300':'text-zinc-700'}`}>{t('command_center', 'Command Center')}</h3>
                <div className="space-y-3">
                  {apartment.status==='vacant' && (
                    <Link to={`/admin/tenants/add?apartmentId=${apartment.id}`} className="w-full px-4 py-3 text-sm font-bold uppercase tracking-widest rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 shadow-md shadow-emerald-500/20 flex items-center justify-center transition-all">
                      <RiUser3Line className="me-2 text-lg"/> {t('log_new_client', 'Log New Client')}
                    </Link>
                  )}
                  {(apartment.status==='occupied'||apartment.tenantName) && (
                    <Link to={`/admin/tenants?apartmentId=${apartment.id}`} className="w-full px-4 py-3 text-sm font-bold uppercase tracking-widest rounded-lg bg-red-600 text-white hover:bg-red-700 shadow-md shadow-red-500/20 flex items-center justify-center transition-all">
                      <RiFileListLine className="me-2 text-lg"/> {t('access_client_data', 'Access Client Data')}
                    </Link>
                  )}
                  {apartment.status==='maintenance' && (
                    <button onClick={()=>toggleApartmentStatus('vacant')} disabled={statusUpdating} className="w-full px-4 py-3 text-sm font-bold uppercase tracking-widest rounded-lg bg-amber-500 text-black hover:bg-amber-400 shadow-md shadow-amber-500/20 flex items-center justify-center transition-all">
                      <RiCheckLine className="me-2 text-lg"/> {t('clear_maintenance', 'Clear Maintenance')}
                    </button>
                  )}
                  <Link to={`/admin/buildings/${apartment.buildingId}`} className={`w-full px-4 py-3 text-sm font-bold uppercase tracking-widest rounded-lg border transition-all flex items-center justify-center ${isDarkMode?'bg-zinc-900 border-zinc-700 text-zinc-300 hover:text-white':'bg-white border-zinc-300 text-zinc-700 hover:bg-zinc-100'}`}>
                    <RiBuilding3Line className="me-2 text-lg"/> {t('return_to_complex', 'Return to Complex')}
                  </Link>
                  <Link to={`/admin/apartments/${apartment.id}/edit`} className={`w-full px-4 py-3 text-sm font-bold uppercase tracking-widest rounded-lg border transition-all flex items-center justify-center ${isDarkMode?'bg-zinc-900 border-zinc-700 text-zinc-300 hover:text-white':'bg-white border-zinc-300 text-zinc-700 hover:bg-zinc-100'}`}>
                    <RiPencilLine className="me-2 text-lg"/> {t('configure_unit', 'Configure Unit')}
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* Contract alert */}
          {apartment.tenantName && isLeaseExpired(apartment) && (
            <div className={`mt-8 p-6 rounded-xl border shadow-lg ${isDarkMode?'bg-red-950/40 border-red-900/50':'bg-red-50 border-red-200'}`}>
              <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-5">
                <div className="flex items-start">
                  <div className="p-3 bg-red-600 text-white rounded-lg shadow-md me-4 shrink-0"><RiAlertLine size={28}/></div>
                  <div>
                    <h3 className={`text-xl font-black uppercase tracking-tight ${isDarkMode?'text-red-400':'text-red-700'}`}>{t('critical_contract_expired', 'Critical: Contract Expired')}</h3>
                    <p className={`mt-1 text-sm font-medium ${isDarkMode?'text-red-200/70':'text-red-800/80'}`}>
                      {t('contract_lapsed_desc', { name: apartment.tenantName }, 'The logistics contract for {{name}} has lapsed and requires immediate renewal or termination.')}
                    </p>
                    <p className={`mt-1.5 text-xs font-mono font-bold px-2 py-0.5 inline-block rounded border ${isDarkMode?'bg-red-900/30 text-red-400 border-red-800':'bg-white text-red-600 border-red-300'}`}>
                      {t('lapsed_on', 'Lapsed On')}: {formatDate(apartment.contractEndDate)}
                    </p>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row items-center gap-4 w-full md:w-auto">
                  {apartment.rentStatus==='overdue' && parseFloat(apartment.balance)>0 && (
                    <div className={`px-5 py-2.5 rounded-lg border text-center min-w-[120px] ${isDarkMode?'bg-zinc-900/80 border-red-900/50':'bg-white border-red-200 shadow-sm'}`}>
                      <p className={`text-[10px] font-black uppercase tracking-widest ${isDarkMode?'text-zinc-500':'text-zinc-500'}`}>{t('ledger_debt', 'Ledger Debt')}</p>
                      <p className={`text-xl font-mono font-black ${isDarkMode?'text-red-400':'text-red-600'}`}>{apartment.balance}</p>
                    </div>
                  )}
                  <Link to={`/admin/tenants/${apartment.tenantId}`} className="w-full sm:w-auto px-6 py-3.5 rounded-lg font-bold uppercase tracking-wider text-sm flex items-center justify-center transition-all bg-red-600 text-white hover:bg-red-700 shadow-md shadow-red-500/20 whitespace-nowrap">
                    <RiFileListLine className="me-2 text-lg"/> {t('resolve_contract', 'Resolve Contract')}
                  </Link>
                </div>
              </div>
            </div>
          )}

          {/* Historical Ledger */}
          <div className={`mt-8 rounded-xl border shadow-lg overflow-hidden ${isDarkMode?'bg-zinc-900 border-zinc-800':'bg-white border-zinc-200'}`}>
            <div className={`p-5 md:p-6 border-b flex items-center ${isDarkMode?'bg-zinc-800/50 border-zinc-700':'bg-zinc-50 border-zinc-100'}`}>
              <RiTimeLine className={`me-3 text-2xl ${isDarkMode?'text-zinc-500':'text-zinc-400'}`}/>
              <div>
                <h3 className={`text-xs font-black uppercase tracking-widest ${isDarkMode?'text-zinc-200':'text-zinc-800'}`}>{t('historical_ledger', 'Historical Ledger')}</h3>
                <p className={`text-xs font-medium mt-0.5 ${isDarkMode?'text-zinc-500':'text-zinc-500'}`}>{t('historical_ledger_desc', 'Past client contracts attached to this unit.')}</p>
              </div>
            </div>
            <div>
              {loadingPastTenants ? (
                <div className="flex justify-center p-10"><div className="h-8 w-8 animate-spin rounded-full border-4 border-zinc-500 border-t-transparent"/></div>
              ) : pastTenants.length===0 ? (
                <div className={`text-center py-12 ${isDarkMode?'bg-zinc-900':'bg-white'}`}>
                  <RiArchiveDrawerLine className={`mx-auto text-5xl mb-3 ${isDarkMode?'text-zinc-700':'text-zinc-300'}`}/>
                  <p className={`text-sm font-bold uppercase tracking-widest ${isDarkMode?'text-zinc-500':'text-zinc-400'}`}>{t('no_historical_records', 'No historical records found.')}</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left whitespace-nowrap">
                    <thead className={`text-[10px] uppercase tracking-widest ${isDarkMode?'bg-zinc-800 text-zinc-500':'bg-zinc-100 text-zinc-500'}`}>
                      <tr>
                        <th className="px-6 py-4">{t('entity', 'Entity')}</th>
                        <th className="px-6 py-4">{t('contract_period', 'Contract Period')}</th>
                        <th className="px-6 py-4 text-end">{t('final_balance', 'Final Balance')}</th>
                        <th className="px-6 py-4 text-center">{t('archive', 'Archive')}</th>
                      </tr>
                    </thead>
                    <tbody className={`divide-y ${isDarkMode?'divide-zinc-800':'divide-zinc-100'}`}>
                      {pastTenants.map(tenant=>(
                        <tr key={tenant.id} className={`transition-colors ${isDarkMode?'hover:bg-zinc-800/50':'hover:bg-zinc-50'}`}>
                          <td className="px-6 py-4">
                            <div className={`font-bold text-sm ${isDarkMode?'text-zinc-200':'text-zinc-900'}`}>{tenant.name}</div>
                            <div className={`font-mono text-xs mt-0.5 ${isDarkMode?'text-zinc-500':'text-zinc-500'}`}>{tenant.phone||t('no_phone', 'No phone')}</div>
                          </td>
                          <td className="px-6 py-4">
                            <div className={`font-mono text-xs font-bold px-2.5 py-1 inline-block rounded-md border ${isDarkMode?'bg-zinc-800 border-zinc-700 text-zinc-300':'bg-white border-zinc-200 text-zinc-700'}`}>
                              {formatDate(tenant.contractStartDate)} <span className="text-zinc-500 mx-1">→</span> {formatDate(tenant.contractEndDate)}
                            </div>
                          </td>
                          <td className="px-6 py-4 text-end">
                            <div className={`font-mono font-black text-sm ${parseFloat(tenant.balance)>0?(isDarkMode?'text-red-400':'text-red-600'):(isDarkMode?'text-zinc-500':'text-zinc-400')}`}>
                              {formatCurrencyTenant(tenant.balance)}
                            </div>
                          </td>
                          <td className="px-6 py-4 text-center">
                            <Link to={`/admin/tenants/${tenant.id}`} title={t('view_archive', 'View Archive')}
                              className={`inline-flex items-center justify-center p-2 rounded-lg transition-colors border ${isDarkMode?'bg-zinc-900 border-zinc-700 text-zinc-400 hover:text-white hover:bg-zinc-800':'bg-white border-zinc-200 text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100'}`}>
                              <RiFileListLine size={16}/>
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <style dangerouslySetInnerHTML={{__html:`
        @keyframes fadeIn { from{opacity:0;transform:translateY(-4px)} to{opacity:1;transform:translateY(0)} }
        .animate-fadeIn { animation: fadeIn 0.3s ease-out forwards; }
      `}}/>
    </div>
  );
};

export default ApartmentDetailView;